import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, keycloakAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  ErrorPage,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { policyEntityReadPermission } from '@backstage-community/plugin-rbac-common';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { ConditionalHomePage } from './components/home/ConditionalHomePage';
import { zambianLightTheme, zambianDarkTheme } from './theme/zambianTheme';
import { OpenHMISPage } from '../../../plugins/openhmis/src/components/OpenHMISPage';
import { DHIS2Page } from '@iswesolutions/plugin-dhis2';
import { OpenEverestPage } from '../../../plugins/openeverest/src/components/OpenEverestPage';
// Import the plugin's routable extension (not the raw component) so the app
// registers the airflow plugin and its backend API client (airflowApiRef);
// otherwise useApi(airflowApiRef) throws and the page renders blank.
import { AirflowPage } from '../../../plugins/airflow/src/plugin';
import { AgentChatPage } from '@aws/genai-plugin-for-backstage';
import { KubernetesPage } from './components/kubernetes/KubernetesPage';
import { AssetRegisterPage } from '../../../plugins/it-asset-register/src/components/AssetRegisterPage';
// Import the plugin's routable extension (not the raw component) so the app
// registers the plugin and its phpIPAM API client.
import { IPAMPage } from '@iswesolutions/plugin-ipam';
import { ProxmoxPage } from '@iswesolutions/plugin-proxmox';
import { MonitoringPage } from '../../../plugins/monitoring/src/components/MonitoringPage';
import { AuditPage } from '../../../plugins/audit/src/components/AuditPage';
import { OdooPage } from '../../../plugins/odoo/src/components/OdooPage';

const app = createApp({
  apis,
  themes: [
    {
      id: 'zambian-light',
      title: 'Zambian Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={zambianLightTheme} children={children} />
      ),
    },
    {
      id: 'zambian-dark',
      title: 'Zambian Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={zambianDarkTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={[
          {
            id: 'github-auth-provider',
            title: 'GitHub',
            message: 'Sign in using GitHub',
            apiRef: githubAuthApiRef,
          },
          {
            id: 'keycloak-auth-provider',
            title: 'Keycloak',
            message: 'Sign in using MOH SSO',
            apiRef: keycloakAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <ConditionalHomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/openhmis" element={<OpenHMISPage />} />
    <Route path="/dhis2" element={<DHIS2Page />} />
    <Route path="/openeverest" element={<OpenEverestPage />} />
    <Route path="/airflow" element={<AirflowPage />} />
    <Route path="/assistant/:agentName" element={<AgentChatPage />} />
    <Route path="/kubernetes" element={<KubernetesPage />} />
    <Route path="/asset-register" element={<AssetRegisterPage />} />
    <Route path="/ipam" element={<IPAMPage />} />
    <Route path="/proxmox" element={<ProxmoxPage />} />
    <Route path="/monitoring" element={<MonitoringPage />} />
    <Route path="/audit" element={<AuditPage />} />
    <Route path="/odoo" element={<OdooPage />} />
    <Route
      path="/rbac"
      element={
        <RequirePermission
          permission={policyEntityReadPermission}
          // policy.entity.read is a resource permission; without a resourceRef
          // usePermission denies it client-side without asking the backend.
          resourceRef={policyEntityReadPermission.resourceType}
          errorPage={
            <ErrorPage
              status="403"
              statusMessage="You don't have access to Administration"
              additionalInfo="Ask a Backstage administrator to add you to the admin group in MOH SSO."
            />
          }
        >
          <RbacPage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
